<template>
	<div
		class="flex flex-col justify-between gap-24px rounded-lg border border-n-100 px-16px py-20px mdp:flex-row mdp:items-center"
	>
		<div class="flex gap-16px">
			<img
				v-if="avatar"
				class="h-48px w-48px rounded-full object-cover"
				:src="avatar"
				alt="user avatar"
				loading="lazy"
			/>
			<div v-else>
				<div class="h-48px w-48px">
					<BaseIcon :name="defaultUser" class="h-full w-full" />
				</div>
			</div>
			<div class="flex flex-col gap-8px">
				<div class="flex items-center gap-8px">
					<span
						class="typography-s2-emp max-w-[170px] truncate mdp:font-semibold"
					>
						{{ name }}
					</span>
					<BaseTag v-if="isVerified" variant="success" size="small">
						<template #leadingIcon>
							<div class="h-16px w-16px">
								<BaseIcon
									:name="privacy"
									class="block h-full w-full fill-g-600"
								/>
							</div>
						</template>
						{{ verifiedText }}
					</BaseTag>
					<BaseTag
						v-else-if="isVerified === false"
						variant="no-action"
						size="small"
					>
						{{ unverifiedText }}
					</BaseTag>
				</div>
				<div class="flex items-center gap-4px">
					<div v-if="starRatingScore" class="flex items-center gap-4px">
						<div class="inline-block h-16px w-16px" v-for="n in 5" :key="n">
							<BaseIcon
								:name="star"
								class="h-full w-full"
								:class="n <= starRatingScore ? 'fill-o-400' : 'fill-n-300'"
							/>
						</div>
					</div>
					<span v-if="ratingNumber" class="typography-c1-reg">
						{{ rankText() }}
					</span>
				</div>
			</div>
		</div>
		<div>
			<div class="mdp:hidden">
				<BaseButton
					variant="primary-inverse"
					size="medium"
					:isFull="true"
					class="group"
					@click="handleClick"
				>
					{{ buttonText }}
					<template #trailingIcon>
						<div class="h-16px w-16px">
							<BaseIcon
								:name="arrowLink"
								class="h-full w-full fill-o-500 group-hover:fill-white"
							/>
						</div>
					</template>
				</BaseButton>
			</div>
			<BaseButton
				@click="handleClick"
				class="hidden mdp:flex"
				variant="tertiary"
				size="none"
			>
				<template #textNoUnderline>{{ buttonText }}</template>
			</BaseButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { PropType } from 'vue'
import BaseTag from '@/components/ui/tag/BaseTag.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	avatar: {
		type: String as PropType<string | null>,
		default: null,
	},
	name: {
		type: String,
		required: true,
	},
	ratingNumber: {
		type: Number as PropType<number | null>,
		default: null,
	},
	starRatingScore: {
		type: Number as PropType<number | null>,
		default: null,
	},
	isVerified: {
		type: Boolean as PropType<boolean | null>,
		default: null,
	},
	verifiedText: {
		type: String,
		required: true,
	},
	unverifiedText: {
		type: String,
		required: true,
	},
	buttonText: {
		type: String,
		required: true,
	},
})

const emit = defineEmits(['click'])

const svgOptions = [
	'arrow_link',
	'privacy_v3',
	'mercari_default_user_icon',
	'star_v1',
]

const arrowLink = ref(svgOptions[0])
const privacy = ref(svgOptions[1])
const defaultUser = ref(svgOptions[2])
const star = ref(svgOptions[3])

const rankText = () => {
	if (!props.ratingNumber) return `(0)`
	return `(${props.ratingNumber})`
}

const handleClick = () => {
	emit('click')
}
</script>
